import { PLAN_DISPLAY, PLAN_KEYS } from "@giftforward/entitlements";
import { InfoOutlined, PersonAddAlt1Outlined, Visibility } from "@mui/icons-material";
import { Alert, IconButton, Snackbar, Tooltip } from "@mui/material";
import { useState } from "react";
import { fetchUsers } from "@/api/users";
import Blotter, { type BlotterConfig, type BlotterData } from "@/components/Blotter";
import type { AppliedFilter, FilterField } from "@/components/BlotterFilterBar";
import ChangeEmailDialog from "@/components/usersOrgs/ChangeEmailDialog";
import InviteUserDialog from "@/components/usersOrgs/InviteUserDialog";
import UserEditDialog from "@/components/usersOrgs/UserEditDialog";
import { usersBlotterColumns } from "@/components/usersOrgs/usersColumns";
import { useImpersonate } from "@/hooks/useImpersonate";
import { usePermissions } from "@/hooks/usePermissions";
import { useRoles } from "@/hooks/useRoles";
import { useUsers } from "@/hooks/useUsers";
import type { ChangeEmailResponse, User, UserActionToast } from "@/interfaces/user";
import { userListParams } from "@/utils/usersOrgs-helper";

// Plan keys, matching subscription_plans.plan_key, labelled as the plan badge reads.
const PLAN_OPTIONS: FilterField["options"] = PLAN_KEYS.map((key) => ({
  value: key,
  label: PLAN_DISPLAY[key].badgeLabel,
}));
const STATUS_OPTIONS: FilterField["options"] = [
  { value: "Enabled", label: "Enabled" },
  { value: "Invited", label: "Invited" },
  { value: "Archived", label: "Archived" },
];

const EMAIL_WARNING_TEXT: Record<ChangeEmailResponse["warnings"][number], string> = {
  old_address_not_removed:
    "the old address is still on the Clerk account and can still sign in — remove it in the Clerk dashboard",
  notification_failed: "the notification email failed",
};

function changeEmailToast(r: ChangeEmailResponse): UserActionToast {
  const message = `Email changed to ${r.user.email}`;
  if (r.warnings.length === 0) return { severity: "success", message };
  const details = r.warnings.map((w) => EMAIL_WARNING_TEXT[w]).join("; ");
  return { severity: "warning", message: `${message}, but ${details}` };
}

/**
 * What the impersonate cell should offer for a row. `null` hides it outright — the action is
 * never offered against yourself, which is also what the API answers (UsersController's
 * "Cannot impersonate yourself", 400), so hiding it reflects an existing rule rather than
 * inventing one.
 *
 * Otherwise the eye appears only when Clerk actually has a user to become. Every other case
 * keeps the cell but swaps in an (i) carrying the reason: a dimmed eye reads as a broken
 * button, and a disabled MUI IconButton does not fire hover events, so its `title` never
 * surfaces in Chrome — the sender was left with no signal at all.
 *
 * `archived` is a real refusal and not a cosmetic one: an archived user keeps their
 * `clerkUserId`, so the eye used to look live, but `findUserByClerkId` requires
 * `archived = false` and the call 404s.
 */
type ImpersonateCell = { allowed: true } | { allowed: false; reason: string };

function impersonateCellFor(u: User, selfId: number | null): ImpersonateCell | null {
  if (selfId !== null && u.id === selfId) return null;
  if (!u.clerkUserId) {
    return {
      allowed: false,
      reason: u.clerkInvitationId
        ? "Invitation still pending — this user gets a Clerk account once they accept it."
        : "This user has no Clerk account, so there is no session to impersonate.",
    };
  }
  if (u.archived) {
    return { allowed: false, reason: "Archived users can't be impersonated. Restore them first." };
  }
  return { allowed: true };
}

/** Data hook for the users blotter — maps blotter query state to the users API. */
function useUsersData(q: {
  search: string;
  filters: AppliedFilter[];
  sortBy: string;
  sortDir: "asc" | "desc";
  page: number;
  limit: number;
}): BlotterData<User> {
  const { data, isLoading } = useUsers({
    ...userListParams({ search: q.search, filters: q.filters }),
    sortBy: q.sortBy,
    sortDir: q.sortDir,
    page: q.page,
    limit: q.limit,
  });
  return {
    data: data?.data ?? [],
    total: data?.total ?? 0,
    totalPages: data?.totalPages ?? 1,
    isLoading,
  };
}

export default function UsersBlotter() {
  const [editUser, setEditUser] = useState<User | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [changeEmailUser, setChangeEmailUser] = useState<User | null>(null);
  const [toast, setToast] = useState<UserActionToast | null>(null);
  const { roles: roleCatalog } = useRoles();
  const { isGfg, can, userId } = usePermissions();
  const impersonate = useImpersonate();
  // Computed once here: Blotter renderers must not call hooks.
  const rowPerms = {
    canEditUsers: can("edit", "USER"),
    isGfgStaff: can("edit", "USER", "all"),
    canChangeEmail: can("change_email", "USER"),
  };

  function handleImpersonate(u: User) {
    if (!u.clerkUserId) return;
    void impersonate.impersonate(u.clerkUserId);
  }

  const config: BlotterConfig<User> = {
    title: "Users",
    searchPlaceholder: "Search and filter users",
    itemNoun: "user",
    minWidth: 1100,
    rowKey: (u) => u.id,
    headerAction: can("create", "USER") ? (
      <IconButton
        onClick={() => setInviteOpen(true)}
        title="Invite user"
        aria-label="Invite user"
        sx={{
          bgcolor: "var(--color-rodeo-black)",
          color: "var(--color-white)",
          borderRadius: "var(--radius-btn)",
          width: 48,
          height: 48,
          "&:hover": { bgcolor: "var(--color-rodeo-black)", opacity: 0.9 },
        }}
      >
        <PersonAddAlt1Outlined />
      </IconButton>
    ) : undefined,
    exportFilename: "users.csv",
    exportFetch: async (q) => {
      const res = await fetchUsers({
        ...userListParams({ search: q.search, filters: q.filters }),
        page: q.page,
        limit: q.limit,
      });
      return { data: res.data, totalPages: res.totalPages };
    },
    useData: useUsersData,
    filterPanel: true,
    filterFields: [
      { id: "status", label: "Status", options: STATUS_OPTIONS },
      {
        // The Role column shows the org-level role, so the filter offers org-level roles.
        id: "role",
        label: "Org Role",
        options: roleCatalog
          .filter((r) => r.level === "org")
          .map((r) => ({ value: r.key, label: r.label })),
      },
      { id: "plan", label: "Plan", options: PLAN_OPTIONS },
      // GFG-user filter is only meaningful to (and only shown to) GFG staff.
      // Both GFG-only: a client never sees GFG staff, and demo organizations' users only exist
      // for GFG staff to impersonate (Users & Orgs → Demo).
      ...(isGfg
        ? [
            { id: "gfg", label: "GFG User", options: [], boolean: true } satisfies FilterField,
            { id: "demo", label: "Demo User", options: [], boolean: true } satisfies FilterField,
          ]
        : []),
    ],
    // Only callers who may impersonate (GFG staff) get the impersonate cell — same gate as
    // UserActions. Client Admins never see it.
    leadingCell: can("start", "IMPERSONATION")
      ? (u) => {
          const cell = impersonateCellFor(u, userId);
          if (!cell) return null;

          if (!cell.allowed) {
            // Not an IconButton: the tooltip has to work, and MUI suppresses hover on a disabled
            // button. A plain span holds the ref and the handlers Tooltip needs, and carries the
            // reason to screen readers as well.
            return (
              <Tooltip title={cell.reason}>
                <span
                  role="img"
                  aria-label={cell.reason}
                  style={{ display: "inline-flex", cursor: "help" }}
                >
                  <InfoOutlined sx={{ fontSize: 18, color: "var(--color-graphite)" }} />
                </span>
              </Tooltip>
            );
          }

          return (
            <Tooltip title={`Impersonate ${u.name}`}>
              <span style={{ display: "inline-flex" }}>
                <IconButton
                  size="small"
                  aria-label={`Impersonate ${u.name}`}
                  disabled={impersonate.isPending}
                  onClick={() => handleImpersonate(u)}
                >
                  <Visibility sx={{ fontSize: 18, color: "var(--color-dark-dust)" }} />
                </IconButton>
              </span>
            </Tooltip>
          );
        }
      : undefined,
    columns: usersBlotterColumns({
      perms: rowPerms,
      onEdit: setEditUser,
      onToast: setToast,
      onChangeEmail: setChangeEmailUser,
    }),
  };

  return (
    <>
      <Blotter config={config} />
      <UserEditDialog user={editUser} onClose={() => setEditUser(null)} />
      <InviteUserDialog open={inviteOpen} onClose={() => setInviteOpen(false)} />
      <ChangeEmailDialog
        user={changeEmailUser}
        onClose={() => setChangeEmailUser(null)}
        onChanged={(r) => setToast(changeEmailToast(r))}
      />
      {/* A warning (Change Email with a step missing) may ask the admin to act in Clerk, so it
          stays until dismissed: no auto-hide, no click-away. */}
      <Snackbar
        open={toast !== null}
        autoHideDuration={toast?.severity === "warning" ? null : 6000}
        onClose={(_, reason) => {
          if (reason === "clickaway" && toast?.severity === "warning") return;
          setToast(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} className="w-full">
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
}
